import React from 'react';
import { ImageIcon } from '@revfluence/fresh-icons/regular/esm';
import { InstagramIcon, TiktokIcon, TwitterIcon, FacebookIcon, YoutubeIcon, PinterestIcon } from '@revfluence/fresh-icons/brands/esm';
import { Card } from '@/shadcn/components/ui/card';
import { Badge } from '@/shadcn/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/shadcn/components/ui/avatar';
import { Checkbox } from '@/shadcn/components/ui/checkbox';
import { Skeleton } from '@/shadcn/components/ui/skeleton';
import { cn } from '@/shadcn/lib/utils';
import { getColorFromName, getInitials } from '@/app/refresh-components/NameCell';

// === MEDIA SUB-COMPONENTS ===

export const ContentPreviewCard = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <Card ref={ref} className={cn('relative w-full aspect-[9/16] group overflow-hidden', className)} {...props}>
      {children}
    </Card>
  ),
);

ContentPreviewCard.displayName = 'ContentPreviewCard';

export const ContentPreviewCardMedia = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('absolute inset-0', className)} {...props}>
      {children}
    </div>
  ),
);

ContentPreviewCardMedia.displayName = 'ContentPreviewCardMedia';

export const ContentPreviewCardSkeleton = () => <Skeleton className="w-full h-full" />;

export const ContentPreviewCardBackground = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & { imageUrl: string }
>(({ className, imageUrl, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('absolute inset-0 w-full h-full bg-cover bg-center blur-md brightness-90 scale-110', className)}
    style={{
      backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
    }}
    {...props}
  />
));

ContentPreviewCardBackground.displayName = 'ContentPreviewCardBackground';

export const ContentPreviewCardImageContainer = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('relative h-full min-h-28 min-w-28 overflow-hidden rounded-lg md:min-h-36 md:min-w-36', className)}
      {...props}
    >
      {children}
    </div>
  ),
);

ContentPreviewCardImageContainer.displayName = 'ContentPreviewCardImageContainer';

export const ContentPreviewCardImage = React.forwardRef<React.ElementRef<'img'>, React.ComponentPropsWithoutRef<'img'>>(
  ({ className, ...props }, ref) => {
    return <img alt="Post content" className={cn('w-full h-full object-contain', className)} {...props} ref={ref} />;
  },
);

ContentPreviewCardImage.displayName = 'ContentPreviewCardImage';

export const ContentPreviewCardImageContent = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('relative h-full w-full', className)} {...props}>
      {children}
    </div>
  ),
);

ContentPreviewCardImageContent.displayName = 'ContentPreviewCardImageContent';

export const ContentPreviewCardPlaceholder = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('w-full h-full bg-slate-800 flex items-center justify-center', className)} {...props}>
      <ImageIcon className="h-16 w-16 text-slate-500" />
    </div>
  ),
);

ContentPreviewCardPlaceholder.displayName = 'ContentPreviewCardPlaceholder';

export const ContentPreviewCardGradient = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'absolute inset-0 bg-gradient-to-b from-[#16282D]/80 via-[#16282D]/20 to-[#16282D]/80 pointer-events-none z-[2]',
        className,
      )}
      {...props}
    />
  ),
);

ContentPreviewCardGradient.displayName = 'ContentPreviewCardGradient';

// === CONTENT SUB-COMPONENTS ===

export const ContentPreviewCardContent = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('relative h-full flex flex-col z-10', className)} {...props}>
      {children}
    </div>
  ),
);

ContentPreviewCardContent.displayName = 'ContentPreviewCardContent';

export const ContentPreviewCardHeader = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('p-4 flex items-center gap-3', className)} {...props}>
      {children}
    </div>
  ),
);

ContentPreviewCardHeader.displayName = 'ContentPreviewCardHeader';

export const ContentPreviewCardAvatarContainer = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center gap-3', className)} {...props}>
      {children}
    </div>
  ),
);

ContentPreviewCardAvatarContainer.displayName = 'ContentPreviewCardAvatarContainer';

export const ContentPreviewCardNetworkIcon = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & { network: string }
>(({ className, network, ...props }, ref) => {
  const getNetworkIcon = () => {
    const networkType = network.toLowerCase();
    switch (networkType) {
      case 'instagram':
        return <InstagramIcon className="h-full w-full" />;
      case 'twitter':
      case 'x':
        return <TwitterIcon className="h-full w-full" />;
      case 'facebook':
        return <FacebookIcon className="h-full w-full" />;
      case 'tiktok':
        return <TiktokIcon className="h-full w-full" />;
      case 'youtube':
        return <YoutubeIcon className="h-full w-full" />;
      case 'pinterest':
        return <PinterestIcon className="h-full w-full" />;
      default:
        return null;
    }
  };

  return (
    <div
      ref={ref}
      className={cn(
        'absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-white flex items-center justify-center p-1',
        className,
      )}
      {...props}
    >
      {getNetworkIcon()}
    </div>
  );
});

ContentPreviewCardNetworkIcon.displayName = 'ContentPreviewCardNetworkIcon';

export const ContentPreviewCardAvatar = React.forwardRef<
  React.ElementRef<typeof Avatar>,
  React.ComponentPropsWithoutRef<typeof Avatar> & {
    networkIcon?: React.ReactNode;
  }
>(({ className, children, networkIcon, ...props }, ref) => (
  <div className="relative">
    <Avatar ref={ref} className={cn('h-8 w-8 ring-2 ring-white', className)} {...props}>
      {children}
    </Avatar>
    {networkIcon}
  </div>
));

ContentPreviewCardAvatar.displayName = 'ContentPreviewCardAvatar';

export const ContentPreviewCardAvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarImage>,
  React.ComponentPropsWithoutRef<typeof AvatarImage> & { alt: string }
>(({ src, alt, ...props }, ref) => <AvatarImage ref={ref} src={src} alt={alt} {...props} />);

ContentPreviewCardAvatarImage.displayName = 'ContentPreviewCardAvatarImage';

export const ContentPreviewCardAvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarFallback>,
  React.ComponentPropsWithoutRef<typeof AvatarFallback> & { name: string }
>(({ name, className, ...props }, ref) => (
  <AvatarFallback
    ref={ref}
    className={cn(
      'flex items-center justify-center h-full w-full text-white font-semibold text-[10px]',
      getColorFromName(name),
      className,
    )}
    {...props}
  >
    {getInitials(name)}
  </AvatarFallback>
));

ContentPreviewCardAvatarFallback.displayName = 'ContentPreviewCardAvatarFallback';

export const ContentPreviewCardAvatarName = React.forwardRef<React.ElementRef<'span'>, React.ComponentPropsWithoutRef<'span'>>(
  ({ className, children, ...props }, ref) => (
    <span ref={ref} className={cn('font-medium text-white drop-shadow-sm', className)} {...props}>
      {children}
    </span>
  ),
);

ContentPreviewCardAvatarName.displayName = 'ContentPreviewCardAvatarName';

export const ContentPreviewCardFooter = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('p-4 space-y-3', className)} {...props}>
      {children}
    </div>
  ),
);

ContentPreviewCardFooter.displayName = 'ContentPreviewCardFooter';

export const ContentPreviewCardNetwork = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & { network: string }
>(({ className, network, ...props }, ref) => (
  <div ref={ref} className={cn(className)} {...props}>
    <Badge variant="secondary" className="capitalize bg-grey-2 text-white">
      {network.toLowerCase()}
    </Badge>
  </div>
));

ContentPreviewCardNetwork.displayName = 'ContentPreviewCardNetwork';

export const ContentPreviewCardActions = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center justify-between text-sm text-white', className)} {...props}>
      {children}
    </div>
  ),
);

ContentPreviewCardActions.displayName = 'ContentPreviewCardActions';

export const ContentPreviewCardLink = React.forwardRef<React.ElementRef<'a'>, React.ComponentPropsWithoutRef<'a'>>(
  ({ className, children, ...props }, ref) => (
    <a
      ref={ref}
      className={cn('text-white hover:underline', className)}
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {children}
    </a>
  ),
);

ContentPreviewCardLink.displayName = 'ContentPreviewCardLink';

export const ContentPreviewCardStats = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center gap-2 sm:gap-4', className)} {...props}>
      {children}
    </div>
  ),
);

ContentPreviewCardStats.displayName = 'ContentPreviewCardStats';

export const ContentPreviewCardStatText = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & { icon: React.ReactNode; text: string }
>(({ className, icon, text, ...props }, ref) => {
  return (
    <div ref={ref} className={cn('flex items-center gap-1 text-xs sm:text-sm', className)} {...props}>
      {icon}
      <span>{text}</span>
    </div>
  );
});

ContentPreviewCardStatText.displayName = 'ContentPreviewCardStatText';

export const ContentPreviewCardStat = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & { icon: React.ReactNode; value: number }
>(({ className, icon, value, ...props }, ref) => {
  const formatter = new Intl.NumberFormat('en', { notation: 'compact' });

  return <ContentPreviewCardStatText ref={ref} className={className} icon={icon} text={formatter.format(value)} {...props} />;
});

ContentPreviewCardStat.displayName = 'ContentPreviewCardStat';

export const ContentPreviewCardHeaderRow = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('flex w-full items-center justify-between', className)} {...props}>
      {children}
    </div>
  ),
);

ContentPreviewCardHeaderRow.displayName = 'ContentPreviewCardHeaderRow';

export const ContentPreviewCardCheckbox = React.forwardRef<
  React.ElementRef<typeof Checkbox>,
  React.ComponentPropsWithoutRef<typeof Checkbox>
>(({ className, ...props }, ref) => (
  <Checkbox
    ref={ref}
    className={cn('h-4 w-4 rounded border-grey-4 text-blue-4 focus-visible:ring-grey-6 bg-grey-0', className)}
    {...props}
  />
));

ContentPreviewCardCheckbox.displayName = 'ContentPreviewCardCheckbox';
